import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';
import { FormlyDialogComponent, FormlyDialogData } from 'ng2-app/components/formly-dialog/formly-dialog.component';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import { Application } from 'app/models/dto/deal-application.model';
import { Deal } from 'app/models/dto/deal.model';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { OfferFormFields } from 'app/offer/offer.formly-fields';
import { Offer } from 'app/services/offers/offer.model';
import { BorrowerValueState } from 'app/store/borrower-value/borrower-value.state';
import { CreateAPIApplication, CreateApplication } from 'app/store/deal/application/application.actions';
import { DealApplicationRequirementState } from 'app/store/deal/deal-application-requirements/deal-application-requirement.state';
import { DealDocumentState } from 'app/store/deal/deal-documents/deal-document.state';
import { CreateDeal, FundDeal } from 'app/store/deal/deal/deal.actions';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { CreateOffer } from 'app/store/offers/offer.actions';
import { formatISO } from 'date-fns';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { ReactivateDealDialogComponent } from '../reactivate-deal-dialog/reactivate-deal-dialog.component';
import { RingDealFormFields } from './ring-deal.formly-fields';

@Component({
  selector: 'app-deal-actions-smart',
  templateUrl: './deal-actions-smart.component.html',
  styleUrls: ['./deal-actions-smart.component.scss']
})
export class DealActionsSmartComponent implements OnInit {

  @Input() bulkSubmitting = false;
  @Input() deal: Deal;
  @Input() offers: Offer[];
  @Input() applications: Application[];

  @Output() submit = new EventEmitter<boolean>();

  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

  offerFormFields = new OfferFormFields;
  ringDealFormFields = new RingDealFormFields;
  offerModel = {};
  ringDealModel: { deal: any, offer: any } = {
    deal: {},
    offer: {}
  };
  teamLenderUsers: { value: number, label: string }[];
  loading = false;
  dateSigned$: Observable<BorrowerValue | null>;
  ringingDeal = false;
  docRequirementsMet$: Observable<boolean>;
  questionRequirementsMet$: Observable<boolean>;

  constructor(
    private dialog: MatDialog,
    private store: Store,
  ) { }

  ngOnInit(): void {
    this.lenderUser$.pipe(take(1)).subscribe(currentLenderUser => {
      this.teamLenderUsers = Object.values(currentLenderUser.team.users).map((user: LenderUser) => ({
          label: user.fullName,
          value: user.id
        }));
    });
    this.dateSigned$ = this.store.select(BorrowerValueState.valueByAlias).pipe(map(filterFn => filterFn('date_signed')));
    this.docRequirementsMet$ = this.store.select(DealDocumentState.requirementsMet)
      .pipe(map(filterFn => filterFn(this.deal.id)));
    this.questionRequirementsMet$ = this.store.select(DealApplicationRequirementState.requirementsMet)
      .pipe(map(filterFn => filterFn(this.deal.id)));
  }

  openOfferDialog() {
    const dialogRef = this.dialog.open(FormlyDialogComponent, {
      data: this.formlyOfferDialogData(),
      width: '60rem',
      panelClass: 'offer-form-dialog'
    });

    dialogRef.componentInstance.saveData.subscribe(data => {
      const offerData = data.formlyConfig.form.value;
      if (offerData.costType) {
        offerData[offerData.costType] = offerData.cost;
      }
      offerData.dealId = this.deal.id;
      this.store.dispatch(new CreateOffer(offerData));
      dialogRef.close();
    });
  }

  openRingDealDialog() {
    const dialogRef = this.dialog.open(FormlyDialogComponent, {
      data: this.formlyRingDealDialogData(),
      width: '60rem',
      panelClass: 'ring-deal-form-dialog'
    });

    dialogRef.componentInstance.saveData.subscribe(data => {
      this.ringingDeal = true;
      this.store.dispatch(new FundDeal(data.formlyConfig.model.offer, data.formlyConfig.model.deal)).pipe(catchError(err => {
        this.ringingDeal = false;
        return of();
      }));
      dialogRef.close();
    });
  }

  openReactivateDealDialog() {
    this.dialog.open(ReactivateDealDialogComponent, {
      data: this.deal,
      width: '25rem',
    });
  }

  hasActiveApplication() {
    return this.applications && this.applications.filter(a => !a.inactive).length > 0;
  }

  sendViaAPI() {
    this.setSubmit(true);
    this.store.dispatch(new CreateAPIApplication(this.deal)).pipe(take(1), catchError(err => {
      this.setSubmit(false);
      return of();
    })).subscribe(() => {
      this.setSubmit(false);
    });
  }

  sendApplication() {
    this.setSubmit(true);
    this.store.dispatch(new CreateApplication(this.deal)).pipe(take(1), catchError(err => {
      this.setSubmit(false);
      return of();
    })).subscribe(() => {
      this.setSubmit(false);
    });
  }

  setSubmit(setVal: boolean) {
    this.loading = setVal;
    this.submit.emit(setVal);
  }

  renewDeal() {
    this.loading = true;
    this.store.dispatch(new CreateDeal({
      parentDealId: this.deal.id,
      borrowerId: this.deal.borrowerId,
      lenderId: this.deal.lenderId,
      lenderUserId: this.deal.lenderUserId,
      loanProductId: this.deal.loanProductId,
      loanProductLenderId: this.deal.loanProductLenderId
    })).pipe(take(1), catchError(err => {
      this.loading = false;
      return of();
    })).subscribe(() => {
      this.loading = false;
    });
  }

  private formlyOfferDialogData(): FormlyDialogData {
    return {
      loading$: of(false),
      title: 'Add New Offer (Offer 1)',
      formlyConfig: {
        model: this.offerModel,
        form: new UntypedFormGroup({}),
        fields: this.deal.loan_product.type !== 'factoring' ? this.offerFormFields.getOfferFields
          : this.offerFormFields.getFactoringFields,
        options: {
          formState: {
            mainModel: this.offerModel
          }
        }
      }
    };
  }

  private formlyRingDealDialogData() {
    const acceptedOffer = this.offers.find(o => o.accepted);
    if (acceptedOffer) {
      this.ringDealModel.deal = { ...this.deal };
      this.ringDealModel.deal.dateClosed = formatISO(new Date(Date.now()));
      this.ringDealModel.deal.created = new Date(this.deal.created);
      this.ringDealModel.offer = acceptedOffer;
      this.ringDealModel.deal.type = this.deal.type;
      this.ringDealModel.deal.closedBy = this.deal.lenderUserId;

      return {
        ignoreDirty: true,
        loading$: of(false),
        title: 'Ring The Bell',
        formlyConfig: {
          model: this.ringDealModel,
          form: new UntypedFormGroup({}),
          fields: this.ringDealFormFields.getFields(
            this.deal.category,
            acceptedOffer,
            this.teamLenderUsers.sort((a, b) => a.label.localeCompare(b.label))
          ),
          options: {
            formState: {
              mainModel: this.ringDealModel
            }
          }
        }
      };
    }
  }
}
